/**
 * Developer tools dropdown + modals for the creative evaluation page.
 *
 * Owns all devtools-related UI state (dropdown open, modal open, copy target)
 * so the parent page component stays focused on orchestration.
 *
 * Extracted from index.tsx per PR #2906 review follow-up:
 * "consider extracting the devtools block — it currently owns several state
 *  atoms and modal rendering" and "query/error modals share structure; a
 *  small reusable modal wrapper could remove duplication."
 */

import { useCallback, useEffect, useState } from "react";
import type { CopyTarget, ErrorLogEntry, EvalImage } from "../../types/creative-eval";
import { formatErrorLog } from "./helpers";
import ExportDialog from "./export-dialog";

// ---------------------------------------------------------------------------
// Reusable modal shell — shared by BQ query, inspect labels, and error log.
// ---------------------------------------------------------------------------

interface DevToolsModalProps {
  title: string;
  ariaLabel: string;
  copyTarget?: CopyTarget;
  copyText?: string;
  copiedTarget: CopyTarget | null;
  onCopy: (target: CopyTarget, text: string) => void;
  onClose: () => void;
  children: React.ReactNode;
}

const DevToolsModal: React.FC<DevToolsModalProps> = ({
  title,
  ariaLabel,
  copyTarget,
  copyText,
  copiedTarget,
  onCopy,
  onClose,
  children
}) => (
  <div className="query-overlay open">
    <button aria-label={ariaLabel} className="query-backdrop" onClick={onClose} type="button" />
    <div className="query-modal">
      <div className="query-modal-header">
        <h3>{title}</h3>
        <div className="query-modal-actions">
          {copyTarget && copyText && (
            <button
              className={`query-copy-btn ${copiedTarget === copyTarget ? "copied" : ""}`}
              onClick={() => onCopy(copyTarget, copyText)}
              type="button"
            >
              {copiedTarget === copyTarget ? "Copied" : "Copy"}
            </button>
          )}
          <button className="query-close-btn" onClick={onClose} type="button">
            Close
          </button>
        </div>
      </div>
      {children}
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// DevToolsPanel — dropdown button + all modals
// ---------------------------------------------------------------------------

interface DevToolsPanelProps {
  debugQuery: string;
  inspectLabelsQuery: string;
  errorLog: ErrorLogEntry[];
  images: EvalImage[];
  selectedForExport: Set<number>;
}

const DevToolsPanel: React.FC<DevToolsPanelProps> = ({
  debugQuery,
  inspectLabelsQuery,
  errorLog,
  images,
  selectedForExport
}) => {
  const [devToolsOpen, setDevToolsOpen] = useState(false);
  const [queryModalOpen, setQueryModalOpen] = useState(false);
  const [labelsModalOpen, setLabelsModalOpen] = useState(false);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [copiedTarget, setCopiedTarget] = useState<CopyTarget | null>(null);

  const handleCopy = useCallback(async (target: CopyTarget, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedTarget(target);
    } catch {
      // clipboard unavailable (e.g. non-HTTPS context)
    }
  }, []);

  useEffect(() => {
    if (!copiedTarget) return;
    const timeout = window.setTimeout(() => setCopiedTarget(null), 1500);
    return () => window.clearTimeout(timeout);
  }, [copiedTarget]);

  const openModal = (setter: (v: boolean) => void) => {
    setter(true);
    setDevToolsOpen(false);
  };

  return (
    <>
      <div className="devtools-wrapper">
        <button className="devtools-btn" onClick={() => setDevToolsOpen((open) => !open)} type="button">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M12 6V4m0 2a2 2 0 1 0 0 4m0-4a2 2 0 1 1 0 4m-6 8a2 2 0 1 0 0-4m0 4a2 2 0 1 1 0-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 1 0 0-4m0 4a2 2 0 1 1 0-4m0 4v2m0-6V4" />
          </svg>
          Developer Tools
          {errorLog.length > 0 && <span className="devtools-badge visible">{errorLog.length}</span>}
        </button>
        {devToolsOpen && (
          <div className="devtools-dropdown open">
            <button className="devtools-item" onClick={() => openModal(setQueryModalOpen)} type="button">
              View BQ Query
            </button>
            {inspectLabelsQuery && (
              <button className="devtools-item" onClick={() => openModal(setLabelsModalOpen)} type="button">
                Inspect Labels
              </button>
            )}
            <button className="devtools-item" onClick={() => openModal(setErrorModalOpen)} type="button">
              Error Logs
            </button>
            {images.length > 0 && (
              <button className="devtools-item" onClick={() => openModal(setExportModalOpen)} type="button">
                Export
              </button>
            )}
          </div>
        )}
      </div>

      {/* BQ query modal */}
      {queryModalOpen && (
        <DevToolsModal
          title="BigQuery Query"
          ariaLabel="Close BigQuery query modal"
          copyTarget="debugQuery"
          copyText={debugQuery}
          copiedTarget={copiedTarget}
          onCopy={handleCopy}
          onClose={() => setQueryModalOpen(false)}
        >
          <div className="query-body">{debugQuery}</div>
        </DevToolsModal>
      )}

      {/* Error log modal */}
      {errorModalOpen && (
        <DevToolsModal
          title="Error Logs"
          ariaLabel="Close error log modal"
          copyTarget={errorLog.length > 0 ? "errorLog" : undefined}
          copyText={errorLog.length > 0 ? formatErrorLog(errorLog) : undefined}
          copiedTarget={copiedTarget}
          onCopy={handleCopy}
          onClose={() => setErrorModalOpen(false)}
        >
          <div className="query-body error-log-body">
            {errorLog.length === 0 ? (
              <div className="error-log-empty">No errors recorded.</div>
            ) : (
              errorLog.toReversed().map((entry, index) => (
                <div className="error-log-entry" key={`${entry.time}-${index}`}>
                  <div className="error-log-time">{new Date(entry.time).toLocaleString()}</div>
                  <div className="error-log-context">{entry.context}</div>
                  <div className="error-log-message">{entry.message}</div>
                  {entry.detail && <div className="error-log-stack">{entry.detail}</div>}
                </div>
              ))
            )}
          </div>
        </DevToolsModal>
      )}

      {/* Inspect labels modal (only available after a save) */}
      {labelsModalOpen && (
        <DevToolsModal
          title="Inspect Labels"
          ariaLabel="Close inspect labels modal"
          copyTarget="inspectLabels"
          copyText={inspectLabelsQuery}
          copiedTarget={copiedTarget}
          onCopy={handleCopy}
          onClose={() => setLabelsModalOpen(false)}
        >
          <div className="query-body">{inspectLabelsQuery}</div>
        </DevToolsModal>
      )}

      {/* Export dialog */}
      {exportModalOpen && (
        <ExportDialog images={images} selectedIds={selectedForExport} onClose={() => setExportModalOpen(false)} />
      )}
    </>
  );
};

export default DevToolsPanel;
